import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import { customerSupportAdminService } from '../../services'
import ShopAutocomplete from './ShopAutocomplete'
import ArticleAutocomplete from './ArticleAutocomplete'
import {
  REPORT_DATE_PRESETS,
  resolveReportDatePreset,
} from './reportDatePresets'

const todayPreset = resolveReportDatePreset('today') || {
  startDate: '',
  endDate: '',
}

function filenameFromDisposition(header) {
  if (!header) return null
  const m = String(header).match(/filename="?([^"]+)"?/i)
  return m?.[1] || null
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'export.csv'
  a.click()
  URL.revokeObjectURL(url)
}

function formatCell(value) {
  if (value == null || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}T/.test(value)
  ) {
    try {
      return new Date(value).toLocaleString()
    } catch {
      return value
    }
  }
  return String(value)
}

export default function QueryBuilderPanel({ onError, onInfo }) {
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [views, setViews] = useState([])
  const [selectedViewId, setSelectedViewId] = useState(null)

  const [shop, setShop] = useState(null)
  const [article, setArticle] = useState(null)
  const [filterValues, setFilterValues] = useState({})
  const [preset, setPreset] = useState('today')
  const [dateFrom, setDateFrom] = useState(todayPreset.startDate)
  const [dateTo, setDateTo] = useState(todayPreset.endDate)
  const [sortBy, setSortBy] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')

  const [running, setRunning] = useState(false)
  const [result, setResult] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  const [exportOpen, setExportOpen] = useState(false)
  const [exportScope, setExportScope] = useState('all')
  const [exportColumns, setExportColumns] = useState({})
  const [exporting, setExporting] = useState(false)

  const selectedView = useMemo(
    () => views.find((v) => v.viewId === selectedViewId) || null,
    [views, selectedViewId],
  )

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setCatalogLoading(true)
      try {
        const data = await customerSupportAdminService.getQueryCatalog()
        if (!cancelled) setViews(data?.views || [])
      } catch (err) {
        onError?.(
          err?.response?.data?.message ||
            err.message ||
            'Failed to load lookup catalog',
        )
      } finally {
        if (!cancelled) setCatalogLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [onError])

  useEffect(() => {
    if (preset === 'custom') return
    const range = resolveReportDatePreset(preset)
    if (range) {
      setDateFrom(range.startDate)
      setDateTo(range.endDate)
    }
  }, [preset])

  useEffect(() => {
    if (!selectedView) return
    setSortBy(selectedView.defaultSortBy || '')
    setSortOrder(selectedView.defaultSortOrder || 'desc')
    setFilterValues({})
    setArticle(null)
    setResult(null)
    setPage(0)
  }, [selectedView?.viewId])

  const setFilter = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }))
  }

  const buildFilters = () => {
    const filters = { ...filterValues }
    const hasArticleControl = selectedView?.filters?.some(
      (f) => f.control === 'article',
    )
    if (hasArticleControl && article?.sku) {
      filters.articleCode = String(article.sku).trim()
    } else if (hasArticleControl && !filters.articleCode) {
      delete filters.articleCode
    }
    // Drop empty
    Object.keys(filters).forEach((k) => {
      if (filters[k] === '' || filters[k] === undefined || filters[k] === null) {
        delete filters[k]
      }
    })
    return filters
  }

  const openExportDialog = () => {
    const cols = result?.columns || selectedView?.columns || []
    const next = {}
    cols.forEach((c) => {
      next[c.key] = true
    })
    setExportColumns(next)
    setExportScope('all')
    setExportOpen(true)
  }

  const runExport = async () => {
    if (!selectedView) return
    const columns = Object.entries(exportColumns)
      .filter(([, on]) => on)
      .map(([k]) => k)
    if (!columns.length) {
      onError?.('Select at least one column')
      return
    }
    setExporting(true)
    onError?.('')
    try {
      const body = {
        viewId: selectedView.viewId,
        orgId: selectedView.requiresOrg ? Number(shop.id) : undefined,
        filters: buildFilters(),
        sortBy: sortBy || selectedView.defaultSortBy,
        sortOrder,
        scope: exportScope,
        page: page + 1,
        limit: rowsPerPage,
        columns,
      }
      if (selectedView.supportsDateRange) {
        body.dateFrom = dateFrom
        body.dateTo = dateTo
      }
      const response = await customerSupportAdminService.exportQuery(body)
      const filename =
        filenameFromDisposition(response.headers?.['content-disposition']) ||
        `${selectedView.viewId}-export.csv`
      downloadBlob(response.data, filename)
      onInfo?.(
        exportScope === 'all'
          ? `Exported all matching rows (${filename})`
          : `Exported this page (${filename})`,
      )
      setExportOpen(false)
    } catch (err) {
      let message =
        err?.response?.data?.message || err.message || 'Export failed'
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text()
          const parsed = JSON.parse(text)
          message = parsed.message || message
        } catch {
          /* keep */
        }
      }
      onError?.(Array.isArray(message) ? message.join(', ') : message)
    } finally {
      setExporting(false)
    }
  }

  const runLookup = async (nextPage = page, nextLimit = rowsPerPage) => {
    if (!selectedView) return
    if (selectedView.requiresOrg && !shop?.id) {
      onError?.('Select a shop first')
      return
    }
    setRunning(true)
    onError?.('')
    try {
      const body = {
        viewId: selectedView.viewId,
        orgId: selectedView.requiresOrg ? Number(shop.id) : undefined,
        filters: buildFilters(),
        sortBy: sortBy || selectedView.defaultSortBy,
        sortOrder,
        page: nextPage + 1,
        limit: nextLimit,
      }
      if (selectedView.supportsDateRange) {
        body.dateFrom = dateFrom
        body.dateTo = dateTo
      }
      const data = await customerSupportAdminService.runQuery(body)
      setResult(data)
      onInfo?.(
        `Loaded ${data?.pagination?.total ?? data?.rows?.length ?? 0} row(s) for ${selectedView.title}`,
      )
    } catch (err) {
      setResult(null)
      onError?.(
        err?.response?.data?.message ||
          err.message ||
          'Lookup failed',
      )
    } finally {
      setRunning(false)
    }
  }

  if (catalogLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (!selectedView) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick a data flow. Filters use shop and article names — not database
          fields.
        </Typography>
        <Grid container spacing={2}>
          {views.map((view) => (
            <Grid item xs={12} sm={6} md={4} key={view.viewId}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea
                  sx={{ height: '100%' }}
                  onClick={() => setSelectedViewId(view.viewId)}
                >
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {view.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {view.purpose}
                    </Typography>
                    {!view.requiresOrg ? (
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        Company-wide (not shop-specific)
                      </Typography>
                    ) : null}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
        {!views.length ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No lookup views available. Check backend permissions or restart the
            API.
          </Alert>
        ) : null}
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            setSelectedViewId(null)
            setResult(null)
          }}
        >
          All lookups
        </Button>
        <Typography variant="h6" fontWeight={700}>
          {selectedView.title}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {selectedView.purpose}
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            {selectedView.requiresOrg ? (
              <ShopAutocomplete
                value={shop}
                onChange={setShop}
                helperText="Required — results are limited to this shop"
              />
            ) : (
              <Alert severity="info">
                This lookup uses the company catalog / DC stock — not a single
                shop.
              </Alert>
            )}

            {selectedView.supportsDateRange ? (
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {selectedView.dateRangeLabel || 'Date range'}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={preset}
                  onChange={(_, v) => v && setPreset(v)}
                >
                  {REPORT_DATE_PRESETS.map((p) => (
                    <ToggleButton key={p.id} value={p.id}>
                      {p.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    value={dateFrom}
                    onChange={(e) => {
                      setPreset('custom')
                      setDateFrom(e.target.value)
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    value={dateTo}
                    onChange={(e) => {
                      setPreset('custom')
                      setDateTo(e.target.value)
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </Stack>
            ) : null}

            <Grid container spacing={2}>
              {selectedView.filters.map((f) => {
                if (f.control === 'shop') return null
                if (f.control === 'article') {
                  return (
                    <Grid item xs={12} sm={6} key={f.key}>
                      <ArticleAutocomplete
                        value={article}
                        onChange={setArticle}
                        label={f.label}
                        required={!!f.required}
                        helperText={f.helperText}
                      />
                    </Grid>
                  )
                }
                if (f.control === 'boolean') {
                  return (
                    <Grid item xs={12} sm={6} key={f.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!filterValues[f.key]}
                            onChange={(e) => setFilter(f.key, e.target.checked)}
                          />
                        }
                        label={f.label}
                      />
                    </Grid>
                  )
                }
                if (f.control === 'enum') {
                  return (
                    <Grid item xs={12} sm={6} key={f.key}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{f.label}</InputLabel>
                        <Select
                          label={f.label}
                          value={filterValues[f.key] || ''}
                          onChange={(e) => setFilter(f.key, e.target.value)}
                        >
                          <MenuItem value="">Any</MenuItem>
                          {(f.options || []).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )
                }
                return (
                  <Grid item xs={12} sm={6} key={f.key}>
                    <TextField
                      fullWidth
                      size="small"
                      label={f.label}
                      value={filterValues[f.key] || ''}
                      onChange={(e) => setFilter(f.key, e.target.value)}
                      helperText={f.helperText}
                      required={!!f.required}
                    />
                  </Grid>
                )
              })}
            </Grid>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  label="Sort by"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  {(selectedView.sorts || []).map((s) => (
                    <MenuItem key={s.key} value={s.key}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Order</InputLabel>
                <Select
                  label="Order"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <MenuItem value="desc">Newest first</MenuItem>
                  <MenuItem value="asc">Oldest first</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disabled={running}
                onClick={() => {
                  setPage(0)
                  void runLookup(0, rowsPerPage)
                }}
              >
                {running ? 'Looking up…' : 'Run lookup'}
              </Button>
              {result ? (
                <Button
                  startIcon={<DownloadIcon />}
                  variant="outlined"
                  disabled={!result.rows?.length && !result.pagination?.total}
                  onClick={openExportDialog}
                >
                  Export CSV
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={exportOpen}
        onClose={() => !exporting && setExportOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Export lookup results</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose how much data to export and which columns to include.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Rows
          </Typography>
          <RadioGroup
            value={exportScope}
            onChange={(e) => setExportScope(e.target.value)}
          >
            <FormControlLabel
              value="page"
              control={<Radio size="small" />}
              label={`This page only (${result?.rows?.length || 0} rows)`}
            />
            <FormControlLabel
              value="all"
              control={<Radio size="small" />}
              label={`All matching rows (${result?.pagination?.total ?? 0} total, up to 2,000)`}
            />
          </RadioGroup>
          <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              Columns
            </Typography>
            <Button
              size="small"
              onClick={() => {
                const next = {}
                ;(result?.columns || selectedView?.columns || []).forEach(
                  (c) => {
                    next[c.key] = true
                  },
                )
                setExportColumns(next)
              }}
            >
              Select all
            </Button>
            <Button
              size="small"
              onClick={() => {
                const next = {}
                ;(result?.columns || selectedView?.columns || []).forEach(
                  (c) => {
                    next[c.key] = false
                  },
                )
                setExportColumns(next)
              }}
            >
              Clear
            </Button>
          </Stack>
          <FormGroup>
            {(result?.columns || selectedView?.columns || []).map((c) => (
              <FormControlLabel
                key={c.key}
                control={
                  <Checkbox
                    size="small"
                    checked={!!exportColumns[c.key]}
                    onChange={(e) =>
                      setExportColumns((prev) => ({
                        ...prev,
                        [c.key]: e.target.checked,
                      }))
                    }
                  />
                }
                label={c.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={exporting || !Object.values(exportColumns).some(Boolean)}
            onClick={() => void runExport()}
          >
            {exporting ? 'Exporting…' : 'Download CSV'}
          </Button>
        </DialogActions>
      </Dialog>

      {running ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {result?.summary?.totalQuantityOnHand != null ? (
        <Alert severity="info">
          Total quantity on hand for article{' '}
          <strong>{result.summary.articleCode}</strong>:{' '}
          <strong>{result.summary.totalQuantityOnHand}</strong> (sum of lots)
        </Alert>
      ) : null}

      {result && !running ? (
        result.rows?.length ? (
          <Card variant="outlined">
            <CardContent sx={{ px: 0, pb: 0 }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {(result.columns || selectedView.columns).map((col) => (
                        <TableCell key={col.key}>{col.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.rows.map((row, idx) => (
                      <TableRow key={idx}>
                        {(result.columns || selectedView.columns).map((col) => (
                          <TableCell key={col.key}>
                            {formatCell(row[col.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <TablePagination
                component="div"
                count={result.pagination?.total ?? result.rows.length}
                page={page}
                onPageChange={(_, p) => {
                  setPage(p)
                  void runLookup(p, rowsPerPage)
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  const next = parseInt(e.target.value, 10)
                  setRowsPerPage(next)
                  setPage(0)
                  void runLookup(0, next)
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            </CardContent>
          </Card>
        ) : (
          <Alert severity="info">
            No rows matched. Try a different shop, article, or date range.
          </Alert>
        )
      ) : null}
    </Stack>
  )
}
